import fc from "@/firebaseConfig";
import firebase from "firebase/app";
import { last } from "lodash";

// Helper function to generate running number for FixedAssetsCount
async function generateRunningNumber(companyId, year) {
  try {
    // Counter document path: Companies/{companyId}/FixedAssetRunNumber/{year}
    const counterRef = fc.companies
      .doc(companyId)
      .collection("FixedAssetRunNumber")
      .doc(year.toString());

    // Use Firestore transaction for atomic increment
    return await fc.db.runTransaction(async (transaction) => {
      let counterDoc;
      try {
        counterDoc = await transaction.get(counterRef);
      } catch (getError) {
        console.error('Error accessing FixedAssetRunNumber document:', getError);
        throw new Error('Permission denied: Unable to access running number counter. Please check Firestore security rules for FixedAssetRunNumber collection.');
      }
      let currentNumber;

      if (counterDoc.exists) {
        const data = counterDoc.data();
        const startNumber = (data.startNumber !== null && data.startNumber !== undefined) ? data.startNumber : 1;
        const maxNumber = (data.maxNumber !== null && data.maxNumber !== undefined) ? data.maxNumber : 9999;
        currentNumber = (data.currentNumber !== null && data.currentNumber !== undefined) ? data.currentNumber : startNumber;

        // Check if currentNumber has reached MaxNumber
        if (currentNumber >= maxNumber) {
          throw new Error(`Maximum running number (${maxNumber}) reached for year ${year}. Please update MaxNumber or start a new year.`);
        }

        // Increment currentNumber
        currentNumber = currentNumber + 1;

        // Update the document with new currentNumber
        transaction.set(counterRef, { 
          currentNumber: currentNumber,
          year: year.toString(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        }, { merge: true });
      } else {
        // First counter for this year - create with default values
        const startNumber = 1;
        const maxNumber = 9999;
        currentNumber = startNumber;

        transaction.set(counterRef, { 
          startNumber: startNumber,
          currentNumber: currentNumber,
          maxNumber: maxNumber,
          year: year.toString(),
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      }

      // Format: FAC-YYYY-NNNN (e.g., FAC-2024-0001)
      const formattedNumber = String(currentNumber).padStart(4, '0');
      return `FAC-${year}-${formattedNumber}`;
    });
  } catch (error) {
    console.error("Error generating running number:", error);
    // Re-throw if it's a max number error
    if (error.message && error.message.includes("Maximum running number")) {
      throw error;
    }
    // Re-throw if it's a permission error
    if (error.message && error.message.includes("Permission denied")) {
      throw error;
    }
    // Fallback: use timestamp-based ID if counter fails
    console.warn("Falling back to timestamp-based ID");
    return `FAC-${year}-${Date.now()}`;
  }
}

export default {
  namespaced: true,
  state: {
    isLoading: false,
    list: [],
    listOrigin: [],
    searchField: "code",
    page: 1,
    count: 0,
    keyword: "",
    fromDate: "",
    toDate: "",
    filterCondition: "",
    emptyList: true,
    stackPage: {},
    currentPageAlgolia: 0,
    assetList: [],
    assetCounts: [],
    departments: [],
    locations: [],
    assetClasses: [],
    assetListOrderBy: { key: "assetCode", value: "desc" },
    assetsCountOrderBy: { key: "createdAt", value: "desc" },
    selectedList: [],
    filterData: {
      departmentID: "",
      locationID: "",
      classID: "",
      fromDate: "",
      toDate: "",
    },
    showPaginationTemplate: true,
    // Firebase pagination state for assets count
    assetsCountPagination: {
      currentPage: 1,
      itemsPerPage: localStorage.getItem("pageLimit") === "auto" ? 10 : parseInt(localStorage.getItem("pageLimit")) || 20,
      totalItems: 0,
      totalPages: 0
    },
    // Store active filters for assets count
    assetsCountFilters: {
      departmentId: null,
      locationId: null,
      date: null
    },
    // Firebase pagination state for asset list
    assetListPagination: {
      currentPage: 1,
      itemsPerPage: localStorage.getItem("pageLimit") === "auto" ? 10 : parseInt(localStorage.getItem("pageLimit")) || 20,
      totalItems: 0,
      totalPages: 0
    },
    // Filters for asset list
    assetListFilters: {
      assetCode: '',
      countStatus: null
    },
    unsubscribeObjects: {
      assetListUnsubscribe: null,
      assetsCountUnsubscribe: null,
      departmentsUnsubscribe: null,
      locationsUnsubscribe: null,
      classesUnsubscribe: null
    }
  },
  
  mutations: {
    setLoading(state, payload) {
      state.isLoading = payload;
    },
    setAssetList(state, payload) {
      state.assetList = payload;
    },
    setAssetCounts(state, payload) {
      state.assetCounts = payload;
    },
    setDepartments(state, payload) {
      state.departments = payload;
    },
    setLocations(state, payload) {
      state.locations = payload;
    },
    setAssetClasses(state, payload) {
      state.assetClasses = payload;
    },
    setFilterData(state, payload) {
      state.filterData = { ...state.filterData, ...payload };
    },
    
    setKeyword(state, payload) {
      state.keyword = payload;
    },
    resetKeyword(state) {
      state.keyword = "";
    },
    setSelectedList(state, { selectedList }) {
      state.selectedList = selectedList;
    },    setUnsubscribeObject(state, { type, unsubscribe }) {
      state.unsubscribeObjects[type] = unsubscribe;
    },
    setAssetListOrderBy(state, { orderBy }) {
      // Validate the orderBy object before setting it
      if (orderBy && typeof orderBy === 'object') {
        state.assetListOrderBy = orderBy;
      } else {
        // Fallback to default if invalid data is passed
        state.assetListOrderBy = { key: "assetCode", value: "desc" };
      }
    },

    setAssetsCountOrderBy(state, { orderBy }) {
      // Validate the orderBy object before setting it
      if (orderBy && typeof orderBy === 'object') {
        state.assetsCountOrderBy = orderBy;
      } else {
        // Fallback to default if invalid data is passed
        state.assetsCountOrderBy = { key: "createdAt", value: "desc" };
      }
    },

    setAssetsCountPagination(state, payload) {
      state.assetsCountPagination = { ...state.assetsCountPagination, ...payload };
    },

    resetAssetsCountPagination(state) {
      state.assetsCountPagination = {
        currentPage: 1,
        itemsPerPage: state.assetsCountPagination.itemsPerPage, // Keep the items per page setting
        totalItems: 0,
        totalPages: 0
      };
    },

    setAssetsCountFilters(state, payload) {
      state.assetsCountFilters = { ...state.assetsCountFilters, ...payload };
    },

    setAssetListPagination(state, payload) {
      state.assetListPagination = { ...state.assetListPagination, ...payload };
    },

    resetAssetListPagination(state) {
      state.assetListPagination = {
        currentPage: 1,
        itemsPerPage: state.assetListPagination.itemsPerPage, // Keep the items per page setting
        totalItems: 0,
        totalPages: 0
      };
    },

    setAssetListFilters(state, payload) {
      state.assetListFilters = { ...state.assetListFilters, ...payload };
    },

    clearAssetListFilters(state) {
      state.assetListFilters = {
        assetCode: '',
        countStatus: null
      };
    },

    // Mutation to update itemsPerPage from localStorage
    updateItemsPerPageFromLocalStorage(state) {
      const pageLimit = localStorage.getItem("pageLimit");
      const newItemsPerPage = pageLimit === "auto" ? 10 : parseInt(pageLimit) || 20;
      
      // Update both pagination states
      state.assetListPagination.itemsPerPage = newItemsPerPage;
      state.assetsCountPagination.itemsPerPage = newItemsPerPage;
    },

  },
  
  actions: {    async fetchAssetList({ commit, state }, { companyId, page = 1, itemsPerPage = null }) {
      // Clear old data before loading new data
      commit("setAssetList", []);
      commit("setLoading", true);

      try {
        // Unsubscribe from previous listeners if they exist
        if (state.unsubscribeObjects.assetListUnsubscribe) {
          state.unsubscribeObjects.assetListUnsubscribe();
        }

        if (!companyId) {
          console.error("Company ID is required to fetch asset list.");
          commit("setLoading", false);
          return;
        }

        // Use pagination settings from state if not provided
        const limit = itemsPerPage || state.assetListPagination.itemsPerPage;
        const currentPage = page;

        // Build the base query
        let query = fc.companies.doc(companyId).collection("DocsFixedAsset");

        // Exclude disposed assets
        query = query.where("status", "!=", "Disposed");

        // Apply filters
        const filters = state.assetListFilters;

        // Order by status first (required for inequality filters), then by selected field
        query = query.orderBy("status", "asc");
        
        if(state.assetListOrderBy && state.assetListOrderBy.key && state.assetListOrderBy.value) {
          // Only add second orderBy if it's different from status
          if (state.assetListOrderBy.key !== "status") {
            query = query.orderBy(state.assetListOrderBy.key, state.assetListOrderBy.value);
          }
        } else {
          // Default ordering if no orderBySelect is set
          query = query.orderBy('assetCode', 'desc');
        }

        // Calculate offset for pagination
        const offset = (currentPage - 1) * limit;

        // Set up real-time listener - fetch all and filter client-side
        const unsubscribe = query.onSnapshot((snapshot) => {
            // Get all documents
            let allAssets = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                assetCode: data.assetCode || "",
                assetName: data.assetName || "",
                assetDepartment: data.assetDepartment || "",
                assetClass: data.assetClass || "",
                assetLocation: data.assetLocation || "",
                lastCountDate: data.lastCountDate || "",
                countStatus: data.countStatus || "",
                imageUrl: data.imageUrl || "",
                link: data.link || "",
              };
            });

            // Apply client-side filters
            if (filters.assetCode && filters.assetCode.trim() !== '') {
              const searchTerm = filters.assetCode.toLowerCase().trim();
              allAssets = allAssets.filter(asset =>
                asset.assetCode.toLowerCase().includes(searchTerm)
              );
            }

            if (filters.countStatus) {
              allAssets = allAssets.filter(asset =>
                asset.countStatus === filters.countStatus
              );
            }

            if (filters.department) {
              allAssets = allAssets.filter(asset =>
                asset.assetDepartment === filters.department
              );
            }

            if (filters.location) {
              allAssets = allAssets.filter(asset =>
                asset.assetLocation === filters.location
              );
            }

            // Calculate pagination after filtering
            const totalItems = allAssets.length;
            const totalPages = Math.ceil(totalItems / limit);

            // Slice for current page
            const startIndex = offset;
            const endIndex = startIndex + limit;
            const paginatedAssets = allAssets.slice(startIndex, endIndex);

            commit("setAssetList", paginatedAssets);
            commit("setAssetListPagination", {
              currentPage,
              totalItems,
              totalPages
            });
            commit("setLoading", false);
        }, error => {
          console.error("Error fetching fixed assets:", error);
          commit("setLoading", false);
        });

        // Store the unsubscribe function
        commit("setUnsubscribeObject", {
          type: "assetListUnsubscribe",
          unsubscribe
        });

      } catch (error) {
        console.error("Error setting up asset list listener:", error);
        commit("setLoading", false);
      }
    },
    async fetchAssetCount({commit, state}, { companyId, departmentId = null, locationId = null, date = null, page = 1, itemsPerPage = null, userEmail = null, isUserRole = false }) {
      try {
          // Clear old data before loading new data
          commit("setAssetCounts", []);
          commit("setLoading", true);

          if (!companyId) {
            console.error("Company ID is required to fetch assets count.");
            commit("setLoading", false);
            return { count: 0 };
          }

          // Store the current filters in state for pagination
          commit("setAssetsCountFilters", {
            departmentId,
            locationId,
            date
          });

          // Unsubscribe from previous listeners if they exist
          if (state.unsubscribeObjects.assetsCountUnsubscribe) {
            state.unsubscribeObjects.assetsCountUnsubscribe();
          }

          // Use pagination settings from state if not provided
          const limit = itemsPerPage || state.assetsCountPagination.itemsPerPage;
          const currentPage = page;

          // Build the base query for counting total
          let countQuery = fc.companies.doc(companyId).collection("FixedAssetsCount");

          // Apply role-based filtering for regular users
          if (isUserRole && userEmail) {
            countQuery = countQuery.where("countBy", "==", userEmail);
          }

          // Apply filters if provided
          if (departmentId) {
            countQuery = countQuery.where("departmentID", "==", departmentId);
          }

          if (locationId) {
            countQuery = countQuery.where("locationID", "==", locationId);
          }

          // Apply date filter if provided
          if (date) {
            const filterDate = new Date(date);
            const startOfDay = new Date(filterDate);
            startOfDay.setHours(0, 0, 0, 0);

            const endOfDay = new Date(filterDate);
            endOfDay.setHours(23, 59, 59, 999);

            countQuery = countQuery
              .where("createdAt", ">=", firebase.firestore.Timestamp.fromDate(startOfDay))
              .where("createdAt", "<=", firebase.firestore.Timestamp.fromDate(endOfDay));
          }

          // Get total count for pagination calculation
          const countSnapshot = await countQuery.get();
          const totalItems = countSnapshot.size;
          const totalPages = Math.ceil(totalItems / limit);

          // Build the paginated query
          let query = fc.companies.doc(companyId).collection("FixedAssetsCount");

          // Apply role-based filtering for regular users
          if (isUserRole && userEmail) {
            query = query.where("countBy", "==", userEmail);
          }

          // Apply same filters
          if (departmentId) {
            query = query.where("departmentID", "==", departmentId);
          }

          if (locationId) {
            query = query.where("locationID", "==", locationId);
          }

          // Apply date filter
          if (date) {
            const filterDate = new Date(date);
            const startOfDay = new Date(filterDate);
            startOfDay.setHours(0, 0, 0, 0);

            const endOfDay = new Date(filterDate);
            endOfDay.setHours(23, 59, 59, 999);

            query = query
              .where("createdAt", ">=", firebase.firestore.Timestamp.fromDate(startOfDay))
              .where("createdAt", "<=", firebase.firestore.Timestamp.fromDate(endOfDay));
          }

          // Apply ordering
          // Note: When using date range filter, Firebase requires orderBy on the filtered field first,
          // or a composite index for other orderBy fields
          if (state.assetsCountOrderBy && state.assetsCountOrderBy.key && state.assetsCountOrderBy.value) {
            // If date filter is active and ordering by a different field, Firebase needs composite index
            if (date && state.assetsCountOrderBy.key !== 'createdAt') {
              console.warn("Date filter active with orderBy on different field - may require Firebase composite index");
            }

            query = query.orderBy(state.assetsCountOrderBy.key, state.assetsCountOrderBy.value);
          } else {
            // Default ordering by createdAt descending
            query = query.orderBy('createdAt', 'desc');
          }

          // Apply offset-based pagination
          // Calculate offset (skip) based on current page
          const offset = (currentPage - 1) * limit;

          // For Firestore, we need to fetch all docs up to our page and slice client-side
          // OR fetch all and paginate client-side (simpler for small datasets)
          // Let's use a hybrid approach: fetch all matching docs, then slice for current page

          // Set up real-time listener for all matching documents
          const unsubscribe = query.onSnapshot((snapshot) => {
            // Get all documents
            const allAssetsCount = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            // Slice for current page
            const startIndex = offset;
            const endIndex = startIndex + limit;
            const paginatedAssetsCount = allAssetsCount.slice(startIndex, endIndex);

            commit("setAssetCounts", paginatedAssetsCount);
            commit("setAssetsCountPagination", {
              currentPage,
              totalItems,
              totalPages
            });

            commit("setLoading", false);
          }, error => {
            console.error("Error fetching assets count:", error);
            commit("setLoading", false);
            return { count: 0, error: error.message };
          });

          // Store the unsubscribe function
          commit("setUnsubscribeObject", {
            type: "assetsCountUnsubscribe",
            unsubscribe
          });

          return { count: totalItems, totalPages };

      }
      catch (error) {
        console.error("Error fetching assets count:", error);
        commit("setLoading", false);
        return { count: 0, error: error.message };
      }
    },

    async fetchDepartments({ commit, state }, { companyId }) {
      try {
        if (state.unsubscribeObjects.departmentsUnsubscribe) {
          state.unsubscribeObjects.departmentsUnsubscribe();
        }
        
        const unsubscribe = fc.companies
          .doc(companyId)
          .collection("FixedAssetDepartment")
          .onSnapshot(snapshot => {
            const departments = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            commit("setDepartments", departments);
          });
        
          
        commit("setUnsubscribeObject", { 
          type: "departmentsUnsubscribe", 
          unsubscribe 
        });
        
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    },
    
    async fetchLocations({ commit, state }, { companyId }) {
      try {
        if (state.unsubscribeObjects.locationsUnsubscribe) {
          state.unsubscribeObjects.locationsUnsubscribe();
        }
        
        const unsubscribe = fc.companies
          .doc(companyId)
          .collection("FixedAssetLocation")
          .onSnapshot(snapshot => {
            const locations = snapshot.docs.map(doc => ({
              id: doc.id,
              name: doc.data().name || ""
            }));
            
            commit("setLocations", locations);
          });
        
        commit("setUnsubscribeObject", { 
            type: "locationsUnsubscribe", 
            unsubscribe 
          });
        // const locations = [
        //   { id: "1", name: "Head Office" },
        //   { id: "2", name: "Branch Office" },
        //   { id: "3", name: "Warehouse" },
        //   { id: "4", name: "Remote Site" }
        // ]
        //   commit("setLocations", locations);
        
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    },
    
    async fetchAssetClasses({ commit, state }, { companyId }) {
      try {
        if (state.unsubscribeObjects.classesUnsubscribe) {
          state.unsubscribeObjects.classesUnsubscribe();
        }
        
        const unsubscribe = fc.companies
          .doc(companyId)
          .collection("AssetClasses")
          .onSnapshot(snapshot => {
            const assetClasses = snapshot.docs.map(doc => ({
              id: doc.id,
              name: doc.data().name || ""
            }));
            
            commit("setAssetClasses", assetClasses);
          });
          
        commit("setUnsubscribeObject", { 
          type: "classesUnsubscribe", 
          unsubscribe 
        });
        
      } catch (error) {
        console.error("Error fetching asset classes:", error);
      }
    },
    
    async addNewAsset({ commit, dispatch }, { companyId, assetData }) {
      try {
        commit("setLoading", true);
          // Generate a unique code if not provided
        if (!assetData.assetCode) {
          // Get current count of assets to generate a sequential code
          const countQuery = await fc.companies
            .doc(companyId)
            .collection("DocsFixedAsset")
            .get();
            
          const count = countQuery.size + 1;
          assetData.assetCode = `FAM${String(count).padStart(6, '0')}`;
        }
        
        // Add timestamp
        assetData.createdAt = fc.firebase.firestore.FieldValue.serverTimestamp();
        assetData.updatedAt = fc.firebase.firestore.FieldValue.serverTimestamp();
          // Add to Firestore
        await fc.companies
          .doc(companyId)
          .collection("DocsFixedAsset")
          .add(assetData);
        
        commit("setLoading", false);
        return { success: true };
      } catch (error) {
        console.error("Error adding new asset:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },
    
    async updateAsset({ commit }, { companyId, assetId, assetData }) {
      try {
        commit("setLoading", true);
        
        // Add update timestamp
        assetData.updatedAt = fc.firebase.firestore.FieldValue.serverTimestamp();
          // Update in Firestore
        await fc.companies
          .doc(companyId)
          .collection("DocsFixedAsset")
          .doc(assetId)
          .update(assetData);
        
        commit("setLoading", false);
        return { success: true };
      } catch (error) {
        console.error("Error updating asset:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },
    
    async deleteAsset({ commit }, { companyId, assetId }) {
      try {
        commit("setLoading", true);
          // Delete from Firestore
        await fc.companies
          .doc(companyId)
          .collection("DocsFixedAsset")
          .doc(assetId)
          .delete();
        
        commit("setLoading", false);
        return { success: true };
      } catch (error) {
        console.error("Error deleting asset:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },
    
    setFilterData({ commit }, data) {
      commit("setFilterData", data);
    },
    
    setKeyword({ commit }, keyword) {
      commit("setKeyword", keyword);
    },
    
    resetKeyword({ commit }) {
      commit("resetKeyword");
    },
    
    setSelectedList({ commit }, selectedList) {
      commit("setSelectedList", { selectedList });
    },
    
    clearAllListeners({ state }) {
      // Cleanup all listeners when component unmounts
      Object.values(state.unsubscribeObjects).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    },

    setAssetListOrderBy({ commit }, { orderBy }) {
      // Validate the orderBy object
      if (!orderBy || typeof orderBy !== 'object') {
        console.warn("Invalid orderBy object, using default");
        orderBy = { key: "assetCode", value: "desc" };
      }

      if (!orderBy.key || !orderBy.value) {
        console.warn("Missing key or value in orderBy, using default");
        orderBy = { key: "assetCode", value: "desc" };
      }

      if (!['asc', 'desc'].includes(orderBy.value)) {
        console.warn("Invalid orderBy value, defaulting to desc");
        orderBy.value = 'desc';
      }

      commit("setAssetListOrderBy", { orderBy });
    },

    setAssetsCountOrderBy({ commit }, { orderBy }) {
      // Validate the orderBy object
      if (!orderBy || typeof orderBy !== 'object') {
        console.warn("Invalid orderBy object, using default");
        orderBy = { key: "createdAt", value: "desc" };
      }

      if (!orderBy.key || !orderBy.value) {
        console.warn("Missing key or value in orderBy, using default");
        orderBy = { key: "createdAt", value: "desc" };
      }

      if (!['asc', 'desc'].includes(orderBy.value)) {
        console.warn("Invalid orderBy value, defaulting to desc");
        orderBy.value = 'desc';
      }

      commit("setAssetsCountOrderBy", { orderBy });
    },

    async goToAssetsCountPage({ dispatch, state }, { companyId, page, userEmail = null, isUserRole = false }) {
      // Fetch the new page with stored filters
      return await dispatch('fetchAssetCount', {
        companyId,
        page,
        itemsPerPage: state.assetsCountPagination.itemsPerPage,
        departmentId: state.assetsCountFilters.departmentId,
        locationId: state.assetsCountFilters.locationId,
        date: state.assetsCountFilters.date,
        userEmail,
        isUserRole
      });
    },

    async goToAssetListPage({ dispatch, state }, { companyId, page }) {
      // Fetch the new page
      return await dispatch('fetchAssetList', {
        companyId,
        page,
        itemsPerPage: state.assetListPagination.itemsPerPage
      });
    },

    async changeAssetListItemsPerPage({ dispatch, commit, state }, { companyId, itemsPerPage }) {
      // Reset pagination when changing items per page
      commit('resetAssetListPagination');
      commit('setAssetListPagination', { itemsPerPage });

      // Fetch the first page with new items per page
      return await dispatch('fetchAssetList', {
        companyId,
        page: 1,
        itemsPerPage
      });
    },

    setAssetListFilters({ commit }, filters) {
      console.log('Setting asset list filters:', filters);
      commit('setAssetListFilters', filters);
    },

    clearAssetListFilters({ commit, dispatch }, { companyId }) {
      console.log('Clearing asset list filters');
      commit('clearAssetListFilters');
      commit('resetAssetListPagination');

      // Fetch data without filters
      return dispatch('fetchAssetList', {
        companyId,
        page: 1
      });
    },

    async changeAssetsCountItemsPerPage({ dispatch, commit, state }, { companyId, itemsPerPage, userEmail = null, isUserRole = false }) {
      console.log(`Changing items per page to ${itemsPerPage}`);

      // Reset pagination when changing items per page
      commit('resetAssetsCountPagination');
      commit('setAssetsCountPagination', { itemsPerPage });

      // Fetch the first page with new items per page and stored filters
      return await dispatch('fetchAssetCount', {
        companyId,
        page: 1,
        itemsPerPage,
        departmentId: state.assetsCountFilters.departmentId,
        locationId: state.assetsCountFilters.locationId,
        date: state.assetsCountFilters.date,
        userEmail,
        isUserRole
      });
    },
    async setFixedAssetsCountByLocation({ commit, state }, { companyId, departmentId, departmentName, locationId,locationName,date, description = '' }) {
      try {
        commit("setLoading", true);

        if (!companyId || !departmentId || !locationId || !date) {
          throw new Error('Company ID, Department ID, Location ID, and Date are required');
        }

        // Validate date format and convert to start and end of day
        const countDate = new Date(date);
        if (isNaN(countDate.getTime())) {
          throw new Error('Invalid date format');
        }

        const startOfDay = new Date(countDate);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(countDate);
        endOfDay.setHours(23, 59, 59, 999);

        // Check if there's already a count session for this location on this date
        const existingCountQuery = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .where("locationID", "==", locationId)
          .where("departmentID", "==", departmentId)
          .where("createdAt", ">=", firebase.firestore.Timestamp.fromDate(startOfDay))
          .where("createdAt", "<=", firebase.firestore.Timestamp.fromDate(endOfDay));

        const existingCountSnapshot = await existingCountQuery.get();
        
        if (!existingCountSnapshot.empty) {
          const existingSession = existingCountSnapshot.docs[0].data();
          throw new Error(`A counting session already exists for location "${locationName}" on ${date}.`);
        }

        // Get all assets for the specified location
        let assetQuery = fc.companies
          .doc(companyId)
          .collection("DocsFixedAsset")
          .where("assetLocationID", "==", locationId)
          .where("status", "!=", "Disposed");
        if (departmentId) {
          assetQuery = assetQuery.where("assetDepartmentID", "==", departmentId);
        }

        const assetSnapshot = await assetQuery.get();
        const assetsInLocation = [];
        
        assetSnapshot.forEach(doc => {
          const data = doc.data();
          assetsInLocation.push({
            id: doc.id,
            assetCode: data.assetCode || "",
            assetName: data.assetName || "",
            assetDepartment: data.assetDepartment || "",
            assetDepartmentID: data.assetDepartmentID || "",
            assetLocation: data.assetLocation || "",
            assetLocationID: data.assetLocationID || "",
            assetClass: data.assetClass || "",
            assetClassID: firebase.firestore.Timestamp.fromDate(new Date(date)) || '',
            status: data.status || "",
            internalid: data.originalNetsuiteData?.internalid || "",
            
          });
        });

        const totalAssets = assetsInLocation.length;

        if (totalAssets === 0) {
          throw new Error('No assets found in the specified location');
        }

        // Generate custom ID for FixedAssetsCount document
       
        const year = new Date(date).getFullYear();

        const customId = await generateRunningNumber(companyId, year)

        // Create FixedAssetsCount document with custom ID
        const countDocRef = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .doc(customId); 

        const countData = {
          id: countDocRef.id,
          taskNumber: customId,
          departmentID: departmentId,
          departmentName: departmentName,
          locationID: locationId,
          locationName: locationName,
          assetsTotal: totalAssets,
          assetsCount: 0, // Initial count
          description: description,
          status: "InProgress",
          countBy:null,
          createdAt: firebase.firestore.Timestamp.fromDate(new Date(date)),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        };

        // Save the count document
        await countDocRef.set(countData, { merge: true });
        // Create assetsList subcollection with all assets from the location
        const batch = fc.db.batch();
        const batchUpdate = fc.db.batch();
        console.log("Assets in location:", assetsInLocation);
        assetsInLocation.forEach(asset => {
          const assetRef = countDocRef.collection("assetsList").doc(asset.id);
          const assetListRef = fc.companies.doc(companyId).collection("DocsFixedAsset").doc(asset.id);
          batch.set(assetRef, {
            ...asset,
            lastCountDate: firebase.firestore.Timestamp.fromDate(new Date(date)),
            countedAt: null,
            countStatus: "Uncounted", // Default status for counting
            notes: ""
          }, { merge: true });
         batchUpdate.set(assetListRef, {
            lastCountDate: firebase.firestore.Timestamp.fromDate(new Date(date)),
            countedAt: null,
            countStatus: "Uncounted", // Default status for counting
            notes: "",
            fixedAssetsCountRef: firebase.firestore.FieldValue.arrayUnion(countDocRef.id)
          }, { merge: true });

        });
        await batch.commit(); 
        await batchUpdate.commit();
        // assetsInLocation.forEach(asset => { 
        //   const assetListRef = assetListDocRef.doc(asset.id); 
        //  batchUpdate.set(assetListRef, { 
        //     lastCountDate: firebase.firestore.Timestamp.fromDate(new Date(date)),
        //     countedAt: null,
        //     countStatus: "Uncounted", // Default status for counting
        //     notes: ""
        //   }, { merge: true });

        // });
        // await batchUpdate.commit();

        console.log(`Created FixedAssetsCount with ${totalAssets} assets for location ${locationId}`);

        commit("setLoading", false);
        
        return {
          success: true,
          countId: countDocRef.id,
          totalAssets: totalAssets,
          assetsInLocation: assetsInLocation
        };

      } catch (error) {
        console.error("Error setting FixedAssetsCount by location:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },
    //fetch assets list by count ID
    async fetchAssetsListByCountId({ commit }, { companyId, countId }) {
      try {
        commit("setLoading", true);

        if (!companyId || !countId) {
          throw new Error('Company ID and Count ID are required');
        }

        const assetsRef = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .doc(countId)
          .collection("assetsList");

        const snapshot = await assetsRef.get();
        const assetsList = [];

        snapshot.forEach(doc => {
          assetsList.push({
            id: doc.id,
            ...doc.data()
          });
        });

        commit("setAssetList", assetsList);
        commit("setLoading", false);

        return {
          success: true,
          assetsList: assetsList,
          count: assetsList.length
        };

      } catch (error) {
        console.error("Error fetching assets list by count ID:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },
//not used
    async updateAssetCountStatus({ commit }, { companyId, countId, assetId, status, notes = '' }) {
      try {
        if (!companyId || !countId || !assetId || !status) {
          throw new Error('Company ID, Count ID, Asset ID, and Status are required');
        }

        const assetRef = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .doc(countId)
          .collection("assetsList")
          .doc(assetId);

        const updateData = {
          countStatus: status,
          notes: notes,
          countedAt: fc.firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: fc.firebase.firestore.FieldValue.serverTimestamp()
        };

        await assetRef.update(updateData);

        // Update the main count document
        await this.dispatch('fixedAssets/updateFixedAssetsCountSummary', {
          companyId,
          countId
        });

        return { success: true };

      } catch (error) {
        console.error("Error updating asset count status:", error);
        return { success: false, error: error.message };
      }
    },
//not used
    async updateFixedAssetsCountSummary({ commit }, { companyId, countId }) {
      try {
        // Get all assets in the count
        const assetsRef = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .doc(countId)
          .collection("assetsList");

        const snapshot = await assetsRef.get();
        let countedAssets = 0;
        let totalAssets = snapshot.size;

        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.countStatus === 'counted' || data.countStatus === 'found') {
            countedAssets++;
          }
        });

        // Update the main count document
        const countRef = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .doc(countId);

        await countRef.update({
          assetsCount: countedAssets,
          assetsTotal: totalAssets,
          updatedAt: fc.firebase.firestore.FieldValue.serverTimestamp(),
          status: countedAssets === totalAssets ? 'completed' : 'inprogress'
        });

        return { success: true, countedAssets, totalAssets };

      } catch (error) {
        console.error("Error updating FixedAssetsCount summary:", error);
        return { success: false, error: error.message };
      }
    },

    async sendAssetsCountEmail({ commit }, { email, message, includeDetails, selectedItems, companyId }) {
      try {
        commit("setLoading", true);

        if (!email || !selectedItems || selectedItems.length === 0 || !companyId) {
          throw new Error('Email, selected items, and company ID are required');
        }

        // Get the user's ID token for authentication
        const user = firebase.auth().currentUser;
        if (!user) {
          throw new Error('User must be authenticated to send email');
        }

        const token = await user.getIdToken();

        // Call the cloud function
        const functionUrl = 'https://us-central1-sld-etax.cloudfunctions.net/sendFixedAssetsCountEmail';

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            company_id: companyId,
            email: email,
            message: message,
            selectedItems: selectedItems,
            includeDetails: includeDetails
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to send email: ${errorText}`);
        }

        const result = await response.json();
        commit("setLoading", false);

        return { success: true, ...result };

      } catch (error) {
        console.error("Error sending assets count email:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },

    async closeAssetsCount({ commit }, { companyId, countId }) {
      try {
        commit("setLoading", true);

        if (!companyId || !countId) {
          throw new Error('Company ID and Count ID are required');
        }

        // Get the count document to get the total assets
        const countRef = fc.companies
          .doc(companyId)
          .collection("FixedAssetsCount")
          .doc(countId);

        const countDoc = await countRef.get();
        if (!countDoc.exists) {
          throw new Error('Assets count session not found');
        }

        const countData = countDoc.data();
        const assetsTotal = countData.assetsTotal;

        // // Get all assets in the assetsList subcollection
        // const assetsRef = countRef.collection("assetsList");
        // const assetsSnapshot = await assetsRef.get();

        // // Update all assets to 'Counted' status using batch
        // const batch = fc.db.batch();

        // assetsSnapshot.forEach(doc => {
        //   const assetRef = assetsRef.doc(doc.id);
        //   batch.update(assetRef, {
        //     countStatus: 'Counted',
        //     countedAt: firebase.firestore.FieldValue.serverTimestamp(),
        //     updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        //   });
        // });

        // // Commit the batch update for all assets
        // await batch.commit();

        // Update the main count document
        await countRef.update({
          status: 'Complete',
          // assetsCount: assetsTotal,
          forceCount: true,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });

        console.log(`Successfully closed assets count ${countId} - All ${assetsTotal} assets marked as counted`);

        commit("setLoading", false);

        return {
          success: true,
          countId: countId,
          totalAssets: assetsTotal,
          message: 'Assets count closed successfully'
        };

      } catch (error) {
        console.error("Error closing assets count:", error);
        commit("setLoading", false);
        return { success: false, error: error.message };
      }
    },

    // Action to sync itemsPerPage from localStorage
    syncItemsPerPageFromLocalStorage({ commit, dispatch, state }, { companyId, userEmail = null, isUserRole = false }) {
      commit('updateItemsPerPageFromLocalStorage');
      
      // If there's an active asset list, refresh it with the new itemsPerPage
      if (companyId && state.assetList.length > 0) {
        dispatch('fetchAssetList', {
          companyId,
          page: 1, // Reset to first page
          itemsPerPage: state.assetListPagination.itemsPerPage
        });
      }
      
      // If there are active asset counts, refresh them with the new itemsPerPage
      if (companyId && state.assetCounts.length > 0) {
        dispatch('fetchAssetCount', {
          companyId,
          page: 1, // Reset to first page
          itemsPerPage: state.assetsCountPagination.itemsPerPage,
          departmentId: state.assetsCountFilters.departmentId,
          locationId: state.assetsCountFilters.locationId,
          date: state.assetsCountFilters.date,
          userEmail,
          isUserRole
        });
      }
    },


  },
  
  getters: {
    getFilteredAssets: (state) => {
      if (!state.keyword) {
        return state.assetList;
      }
      
      const searchTerm = state.keyword.toLowerCase();
      return state.assetList.filter(asset => 
        asset.name.toLowerCase().includes(searchTerm) || 
        asset.code.toLowerCase().includes(searchTerm) || 
        asset.department.toLowerCase().includes(searchTerm) ||
        asset.class.toLowerCase().includes(searchTerm) ||
        asset.location.toLowerCase().includes(searchTerm)
      );
    },
    
    getDepartmentById: (state) => (id) => {
      const dept = state.departments.find(dep => dep.id === id);
      return dept ? dept.name : '';
    },
    
    getLocationById: (state) => (id) => {
      const loc = state.locations.find(loc => loc.id === id);
      return loc ? loc.name : '';
    },
    
    getClassById: (state) => (id) => {
      const cls = state.assetClasses.find(cls => cls.id === id);
      return cls ? cls.name : '';
    }
  }
};

